import { useEffect, useMemo, useRef, useState } from "react";
import api from "../services/axiosInstance";
import { backendPort } from "../components/Fungsiku";

const toUpperTrim = (v) => String(v || "").trim().toUpperCase();
const toLowerTrim = (v) => String(v || "").trim().toLowerCase();
const MAX_MODAL_CARI_BAN_ITEMS = 1000;
const REQ_GROUP_BULK_PICKER_ENRICH = "modal-bulk-picker-enrich";
const REQ_GROUP_SINGLE_PICKER_ENRICH = "modal-single-picker-enrich";
const REQ_GROUP_MT_PICKER_ENRICH = "modal-mt-picker-enrich";
const isBanStatusBlockedByMt = (statusRaw) => {
  const status = toUpperTrim(statusRaw);
  return status.endsWith("_OUT_BYMT") || status.endsWith("_NON_AKTIF_BYMT");
};
const normalizeStatusList = (list = []) => {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((v) => {
    const s = toUpperTrim(v);
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  });
  return out;
};

export function ModalCariMT({
  open,
  onClose,
  onSelect,
  showNotif,
  enrichFields = [],
  globalOpsi,
  opsiMTSource = null,
  kodeCabang = "",
  treatPusatAsAll = true,
  allowNonAktifSelection = false,
  title = "Cari MT",
  subtitle = "Klik satu data MT untuk langsung memilih.",
}) {
  const [filterText, setFilterText] = useState("");
  const [loadingPick, setLoadingPick] = useState(false);
  const enrichFieldsNormalized = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(enrichFields) ? enrichFields : [])
            .map((x) => String(x || "").trim())
            .filter(Boolean)
        )
      ),
    [enrichFields]
  );

  useEffect(() => {
    if (open) setFilterText("");
  }, [open]);

  const pilihan = useMemo(() => {
    const allMt = Array.isArray(opsiMTSource)
      ? opsiMTSource
      : (Array.isArray(globalOpsi?.opsiMT) ? globalOpsi.opsiMT : []);
    const cabang = String(kodeCabang || "").trim();
    const byCabang = (!cabang || (treatPusatAsAll && cabang === "C-1"))
      ? allMt
      : allMt.filter((x) => String(x?.kode_cabang || "").trim() === cabang);

    const base = byCabang
      .map((x) => ({
        id_mt: String(x?.id_mt || "").trim(),
        nopol: String(x?.nopol || "").trim(),
        kode_cabang: String(x?.kode_cabang || "").trim(),
      }))
      .filter((x) => x.id_mt);

    const q = String(filterText || "").trim().toLowerCase();
    const filtered = !q
      ? base
      : base.filter((x) => x.id_mt.toLowerCase().includes(q) || x.nopol.toLowerCase().includes(q));
    if (Array.isArray(opsiMTSource)) {
      console.log('[DBG][ModalCariMT] opsiMTSource filter', {
        kodeCabang: cabang || '(kosong)',
        treatPusatAsAll,
        sourceCount: allMt.length,
        byCabangCount: byCabang.length,
        q,
        resultCount: filtered.length
      });
    }
    return filtered;
  }, [globalOpsi, opsiMTSource, kodeCabang, filterText, treatPusatAsAll]);

  const notifyError = (msg) => {
    if (typeof showNotif === "function") {
      showNotif("error", msg);
      return;
    }
    if (typeof window !== "undefined" && typeof window.alert === "function") {
      window.alert(msg);
    }
  };

  const enrichSingleMt = async (row) => {
    const idMt = String(row?.id_mt || "").trim();
    if (!idMt) return null;
    setLoadingPick(true);
    try {
      api.cancelPendingRequests({
        group: REQ_GROUP_MT_PICKER_ENRICH,
        reason: "enrich selected mt row",
      });
      const sourceRow = {
        id_mt: idMt,
        nopol: String(row?.nopol || "").trim(),
        kode_cabang: String(row?.kode_cabang || "").trim(),
      };
      const { data } = await api.post(`${backendPort}/api/main/enrich_selected_mt_rows`, {
        selected_ids: [idMt],
        selected_rows: [sourceRow],
        requested_fields: enrichFieldsNormalized,
      }, {
        requestGroup: REQ_GROUP_MT_PICKER_ENRICH,
        timeout: 120000,
      });
      if (data?.cache_mismatch) {
        notifyError("Lakukan Refresh Cache untuk Mendapatkan data Terbaru");
        return null;
      }
      const out = Array.isArray(data?.rows) ? data.rows[0] : null;
      if (!out) return null;
      const mtStatus = String(out?.status || "").trim();
      if (!allowNonAktifSelection && mtStatus.toUpperCase() !== "AKTIF") {
        notifyError("MT berstatus Non Aktif");
        return null;
      }
      return {
        id_mt: String(out?.id_mt || "").trim(),
        nopol: String(out?.nopol || "").trim(),
        kode_cabang: String(out?.kode_cabang || "").trim(),
        ...(mtStatus ? { status: mtStatus } : {}),
        ...(out?.roda != null ? { roda: Number(out?.roda || 0) } : {}),
        ...(out?.cadangan != null ? { cadangan: Number(out?.cadangan || 0) } : {}),
      };
    } catch (err) {
      if (api.isCanceledError(err)) return null;
      if (err?.response?.data?.cache_mismatch) {
        notifyError("Lakukan Refresh Cache untuk Mendapatkan data Terbaru");
        return null;
      }
      notifyError(err?.response?.data?.message || "Gagal memuat detail MT terpilih.");
      return null;
    } finally {
      setLoadingPick(false);
    }
  };

  if (!open) return null;

  return (
    <div className="page16-modal-backdrop" role="dialog" aria-modal="true">
      <div className="page16-modal-card" style={{ maxWidth: "min(560px, 94vw)", width: "94vw" }}>
        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
          <div>
            <h5 className="mb-1">{title}</h5>
            <small className="text-muted">{subtitle}</small>
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Tutup
          </button>
        </div>
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label">Filter</label>
            <input
              type="text"
              className="form-control"
              placeholder="masukkan no. pol"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>
          <div className="col-12">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                gap: "0.35rem",
                maxHeight: "320px",
                overflowY: "auto",
              }}
            >
              {pilihan.map((mt) => (
                <button
                  key={`pick-mt-${mt.id_mt}`}
                  type="button"
                  className="btn btn-sm"
                  style={{
                    background: "#0f2d5c",
                    color: "#fff",
                    border: "1px solid #0f2d5c",
                    padding: "0.2rem 0.32rem",
                    fontSize: "0.74rem",
                    lineHeight: 1.2,
                  }}
                  onClick={async () => {
                    if (loadingPick) return;
                    const selected = await enrichSingleMt(mt);
                    if (!selected) return;
                    onSelect?.(selected);
                    onClose?.();
                  }}
                  disabled={loadingPick}
                >
                  {`${mt.nopol || "-"}`}
                </button>
              ))}
            </div>
            {!pilihan.length && <small className="text-muted d-block mt-2">Data MT tidak ditemukan.</small>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ModalCariBan({
  open,
  onClose,
  onSelect,
  showNotif,
  enrichFields = [],
  globalOpsi,
  kodeCabang = "",
  title = "Cari Ban",
  subtitle = "Klik satu data ban untuk langsung memilih.",
  enableKodeSementara = false,
  defaultKodeSementara = true,
}) {
  const [filterText, setFilterText] = useState("");
  const [kodeSementaraOn, setKodeSementaraOn] = useState(Boolean(defaultKodeSementara));
  const [loadingPick, setLoadingPick] = useState(false);
  const enrichFieldsNormalized = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(enrichFields) ? enrichFields : [])
            .map((x) => String(x || "").trim())
            .filter(Boolean)
        )
      ),
    [enrichFields]
  );

  useEffect(() => {
    if (open) {
      setFilterText("");
      setKodeSementaraOn(Boolean(defaultKodeSementara));
    }
  }, [open, defaultKodeSementara]);

  const pilihan = useMemo(() => {
    const allBan = Array.isArray(globalOpsi?.opsiBan) ? globalOpsi.opsiBan : [];
    const cabang = String(kodeCabang || "").trim();
    const byCabang = (!cabang || cabang === "C-1")
      ? allBan
      : allBan.filter((x) => {
          const k = String(x?.kode_cabang || "").trim();
          return !k || k === cabang;
        });
    const uniq = [];
    const seen = new Set();
    byCabang.forEach((x) => {
      const idBan = Number(x?.id_ban || 0);
      if (!idBan || seen.has(idBan)) return;
      seen.add(idBan);
      uniq.push({
        id_ban: idBan,
        seri_ban: String(x?.seri_ban || "").trim(),
        kode_cabang: String(x?.kode_cabang || "").trim(),
        status_now: String(x?.status_now || x?.status || "").trim(),
        place_now: String(x?.place_now || x?.place || "").trim(),
      });
    });

    const normalized = String(filterText || "").trim();
    const activeFilter = (enableKodeSementara && kodeSementaraOn) ? `SMTR#${normalized}` : normalized;
    const q = String(activeFilter || "").trim().toLowerCase();
    const filtered = !q
      ? uniq
      : uniq.filter((x) => String(x.id_ban).includes(q) || String(x.seri_ban || "").toLowerCase().includes(q));

    return filtered
      .filter((x) => !isBanStatusBlockedByMt(x?.status_now))
      .slice(0, MAX_MODAL_CARI_BAN_ITEMS);
  }, [globalOpsi, kodeCabang, filterText, enableKodeSementara, kodeSementaraOn]);

  const enrichSingleRow = async (row) => {
    const idBan = Number(row?.id_ban || 0);
    if (!idBan) return null;
    setLoadingPick(true);
    try {
      api.cancelPendingRequests({
        group: REQ_GROUP_SINGLE_PICKER_ENRICH,
        reason: "enrich selected single ban row",
      });
      const sourceRow = {
        id_ban: idBan,
        seri_ban: String(row?.seri_ban || "").trim(),
        kode_cabang: String(row?.kode_cabang || "").trim(),
        status_now: String(row?.status_now || row?.status || "").trim(),
        place_now: String(row?.place_now || row?.place || "").trim(),
      };
      const { data } = await api.post(`${backendPort}/api/main/enrich_selected_ban_rows`, {
        selected_ids: [idBan],
        selected_rows: [sourceRow],
        requested_fields: enrichFieldsNormalized,
      }, {
        requestGroup: REQ_GROUP_SINGLE_PICKER_ENRICH,
        timeout: 120000,
      });
      if (data?.cache_mismatch) {
        showNotif?.("error", "Lakukan Refresh Cache untuk Mendapatkan data Terbaru");
        return null;
      }
      const rowOut = Array.isArray(data?.rows) ? data.rows[0] : null;
      if (!rowOut) return null;
      const statusNow = String(rowOut?.status_now || "").trim();
      if (isBanStatusBlockedByMt(statusNow)) {
        showNotif?.("error", "Ban berstatus out/nonaktif by MT tidak bisa ditransaksikan.");
        return null;
      }
      return {
        id_ban: Number(rowOut?.id_ban || 0),
        seri_ban: String(rowOut?.seri_ban || "").trim(),
        kode_cabang: String(rowOut?.kode_cabang || "").trim(),
        status_now: statusNow,
        place_now: String(rowOut?.place_now || "").trim(),
        ...(rowOut?.merk != null ? { merk: String(rowOut?.merk || "").trim() } : {}),
        ...(rowOut?.ukuran != null ? { ukuran: String(rowOut?.ukuran || "").trim() } : {}),
        ...(rowOut?.td != null ? { td: String(rowOut?.td || "").trim() } : {}),
        ...(rowOut?.id_suplier != null ? {
          suplier: String(rowOut?.id_suplier || rowOut?.suplier || "").trim(),
          id_suplier: String(rowOut?.id_suplier || "").trim(),
        } : {}),
        ...(rowOut?.event_terakhir != null ? { event_terakhir: String(rowOut?.event_terakhir || "").trim() } : {}),
      };
    } catch (err) {
      if (api.isCanceledError(err)) return null;
      if (err?.response?.data?.cache_mismatch) {
        showNotif?.("error", "Lakukan Refresh Cache untuk Mendapatkan data Terbaru");
        return null;
      }
      showNotif?.("error", err?.response?.data?.message || "Gagal memuat detail ban terpilih.");
      return null;
    } finally {
      setLoadingPick(false);
    }
  };

  if (!open) return null;

  return (
    <div className="page16-modal-backdrop" role="dialog" aria-modal="true">
      <div className="page16-modal-card" style={{ maxWidth: "min(560px, 94vw)", width: "94vw" }}>
        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
          <div>
            <h5 className="mb-1">{title}</h5>
            <small className="text-muted">{subtitle}</small>
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Tutup
          </button>
        </div>
        <div className="row g-3">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <label className="form-label mb-0">Filter</label>
              {enableKodeSementara && (
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted">Kode Sementara</small>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={kodeSementaraOn}
                      onChange={(e) => setKodeSementaraOn(Boolean(e.target.checked))}
                    />
                    <label className="form-check-label small">{kodeSementaraOn ? "On" : "Off"}</label>
                  </div>
                </div>
              )}
            </div>
            <input
              type="text"
              className="form-control"
              placeholder={(enableKodeSementara && kodeSementaraOn) ? "SMTR#..." : "masukkan no. seri ban"}
              value={(enableKodeSementara && kodeSementaraOn) ? `SMTR#${filterText}` : filterText}
              onChange={(e) => {
                const raw = String(e.target.value || "");
                if (!(enableKodeSementara && kodeSementaraOn)) {
                  setFilterText(raw);
                  return;
                }
                const prefix = "SMTR#";
                if (raw.toUpperCase().startsWith(prefix)) {
                  setFilterText(raw.slice(prefix.length));
                  return;
                }
                setFilterText(raw);
              }}
            />
          </div>
          <div className="col-12">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                gap: "0.35rem",
                maxHeight: "320px",
                overflowY: "auto",
              }}
            >
              {pilihan.map((ban) => (
                <button
                  key={`pick-ban-${ban.id_ban}`}
                  type="button"
                  className="btn btn-sm"
                  style={{
                    background: "#0f2d5c",
                    color: "#fff",
                    border: "1px solid #0f2d5c",
                    padding: "0.2rem 0.32rem",
                    fontSize: "0.74rem",
                    lineHeight: 1.2,
                  }}
                  onClick={async () => {
                    if (loadingPick) return;
                    const selected = await enrichSingleRow(ban);
                    if (!selected) return;
                    onSelect?.(selected);
                    onClose?.();
                  }}
                  disabled={loadingPick}
                >
                  {`${ban.seri_ban || "-"}`}
                </button>
              ))}
            </div>
            {!pilihan.length && <small className="text-muted d-block mt-2">Data ban tidak ditemukan.</small>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ModalCariBanMulti({
  open,
  onClose,
  onSubmit,
  showNotif,
  globalOpsi = null,
  opsiBanSource = null,
  kodeCabang = "",
  initialSelectedIds = [],
  initialSelectedRows = [],
  forcedLocationFilters = null,
  defaultLocationFilters = null,
  disableLocationFilterControls = false,
  blockedLocationFilters = [],
  enrichFields = [],
}) {
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(80);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [activeStatusFilters, setActiveStatusFilters] = useState([]);
  const [activeLocationFilters, setActiveLocationFilters] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedRowMap, setSelectedRowMap] = useState({});
  const initializingFiltersRef = useRef(false);

  const totalPages = useMemo(() => {
    const t = Number(total || 0);
    const s = Number(pageSize || 1);
    return Math.max(1, Math.ceil(t / s));
  }, [total, pageSize]);

  const selectedSet = useMemo(
    () => new Set((Array.isArray(selectedIds) ? selectedIds : []).map((x) => Number(x)).filter((x) => x > 0)),
    [selectedIds]
  );
  const forcedLocationsNormalized = useMemo(() => {
    if (!Array.isArray(forcedLocationFilters)) return [];
    const allowed = new Set(["gudang", "pabrik", "otf"]);
    return Array.from(
      new Set(
        forcedLocationFilters
          .map((x) => String(x || "").trim().toLowerCase())
          .filter((x) => allowed.has(x))
      )
    );
  }, [forcedLocationFilters]);
  const defaultLocationsNormalized = useMemo(() => {
    if (!Array.isArray(defaultLocationFilters)) return [];
    const allowed = new Set(["gudang", "pabrik", "otf"]);
    return Array.from(
      new Set(
        defaultLocationFilters
          .map((x) => String(x || "").trim().toLowerCase())
          .filter((x) => allowed.has(x))
      )
    );
  }, [defaultLocationFilters]);
  const blockedLocationsNormalized = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(blockedLocationFilters) ? blockedLocationFilters : [])
            .map((x) => String(x || "").trim().toLowerCase())
            .filter((x) => ["gudang", "pabrik", "otf"].includes(x))
        )
      ),
    [blockedLocationFilters]
  );
  const effectiveLocationFilters = useMemo(
    () => (forcedLocationsNormalized.length ? forcedLocationsNormalized : activeLocationFilters),
    [forcedLocationsNormalized, activeLocationFilters]
  );
  const forcedLocationsDep = useMemo(
    () => forcedLocationsNormalized.join("|"),
    [forcedLocationsNormalized]
  );
  const defaultLocationsDep = useMemo(
    () => defaultLocationsNormalized.join("|"),
    [defaultLocationsNormalized]
  );
  const effectiveLocationFiltersDep = useMemo(
    () => effectiveLocationFilters.join("|"),
    [effectiveLocationFilters]
  );
  const allLocalRows = useMemo(() => {
    const raw = Array.isArray(opsiBanSource)
      ? opsiBanSource
      : (Array.isArray(globalOpsi?.opsiBan) ? globalOpsi.opsiBan : []);
    const cabang = String(kodeCabang || "").trim();
    const byCabang = (!cabang || cabang === "C-1")
      ? raw
      : raw.filter((x) => {
          const k = String(x?.kode_cabang || "").trim();
          return !k || k === cabang;
        });
    const uniq = [];
    const seen = new Set();
    byCabang.forEach((row) => {
      const idBan = Number(row?.id_ban || 0);
      if (!idBan || seen.has(idBan)) return;
      const statusNow = String(row?.status_now || row?.status || "").trim();
      if (isBanStatusBlockedByMt(statusNow)) return;
      seen.add(idBan);
      uniq.push({
        id_ban: idBan,
        seri_ban: String(row?.seri_ban || "").trim(),
        kode_cabang: String(row?.kode_cabang || "").trim(),
        status_now: statusNow,
        place_now: String(row?.place_now || row?.place || "").trim(),
      });
    });
    return uniq;
  }, [opsiBanSource, globalOpsi, kodeCabang]);
  const localFilteredRows = useMemo(() => {
    const statusSet = new Set(
      (Array.isArray(activeStatusFilters) ? activeStatusFilters : [])
        .map((x) => toUpperTrim(x))
        .filter(Boolean)
    );
    const locations = forcedLocationsNormalized.length ? forcedLocationsNormalized : activeLocationFilters;
    const locationSet = new Set((Array.isArray(locations) ? locations : []).map((x) => toLowerTrim(x)).filter(Boolean));
    const q = String(keyword || "").trim().toLowerCase();
    return allLocalRows.filter((row) => {
      const statusNow = toUpperTrim(row?.status_now);
      if (statusSet.size > 0 && !statusSet.has(statusNow)) return false;
      const place = String(row?.place_now || "").trim().toLowerCase();
      const locKey = place.startsWith("gudang#") ? "gudang" : (place.startsWith("pabrik#") ? "pabrik" : (/^k-/i.test(place) ? "otf" : ""));
      if (locationSet.size > 0 && !locationSet.has(locKey)) return false;
      if (!q) return true;
      return String(row?.id_ban || "").includes(q) || String(row?.seri_ban || "").toLowerCase().includes(q);
    });
  }, [
    allLocalRows,
    activeStatusFilters,
    activeLocationFilters,
    forcedLocationsNormalized,
    keyword,
  ]);

  const enrichFieldsNormalized = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(enrichFields) ? enrichFields : [])
            .map((x) => String(x || "").trim())
            .filter(Boolean)
        )
      ),
    [enrichFields]
  );

  const enrichSelectedRows = async ({ ids = [], sourceRows = [] } = {}) => {
    setLoading(true);
    try {
      api.cancelPendingRequests({
        group: REQ_GROUP_BULK_PICKER_ENRICH,
        reason: "enrich selected ban rows",
      });
      const { data } = await api.post(`${backendPort}/api/main/enrich_selected_ban_rows`, {
        selected_ids: ids,
        selected_rows: sourceRows,
        requested_fields: enrichFieldsNormalized,
      }, {
        requestGroup: REQ_GROUP_BULK_PICKER_ENRICH,
        timeout: 120000,
      });
      if (data?.cache_mismatch) {
        showNotif?.("error", "Lakukan Refresh Cache untuk Mendapatkan data Terbaru");
        return null;
      }
      const rowsRaw = Array.isArray(data?.rows) ? data.rows : [];
      return rowsRaw.map((row) => ({
        id_ban: Number(row?.id_ban || 0),
        seri_ban: String(row?.seri_ban || "").trim(),
        kode_cabang: String(row?.kode_cabang || "").trim(),
        status_now: String(row?.status_now || "").trim(),
        place_now: String(row?.place_now || "").trim(),
        ...(row?.merk != null ? { merk: String(row?.merk || "").trim() } : {}),
        ...(row?.ukuran != null ? { ukuran: String(row?.ukuran || "").trim() } : {}),
        ...(row?.td != null ? { td: String(row?.td || "").trim() } : {}),
        ...(row?.id_suplier != null ? {
          suplier: String(row?.id_suplier || row?.suplier || "").trim(),
          id_suplier: String(row?.id_suplier || "").trim(),
        } : {}),
        ...(row?.event_terakhir != null ? { event_terakhir: String(row?.event_terakhir || "").trim() } : {}),
        ...(row?.status_sebelum_perlu_repair != null ? {
          status_sebelum_perlu_repair: String(row?.status_sebelum_perlu_repair || "").trim(),
        } : {}),
      })).filter((row) => row.id_ban > 0 && !isBanStatusBlockedByMt(row?.status_now));
    } catch (err) {
      if (api.isCanceledError(err)) return null;
      if (err?.response?.data?.cache_mismatch) {
        showNotif?.("error", "Lakukan Refresh Cache untuk Mendapatkan data Terbaru");
        return null;
      }
      showNotif?.("error", err?.response?.data?.message || "Gagal memuat detail ban terpilih.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const forcedLocationsForInit = forcedLocationsDep ? forcedLocationsDep.split("|").filter(Boolean) : [];
    const defaultLocationsForInit = defaultLocationsDep ? defaultLocationsDep.split("|").filter(Boolean) : [];
    setKeyword("");
    setPage(1);
    setTotal(0);
    setSelectedIds(
      Array.from(new Set((Array.isArray(initialSelectedIds) ? initialSelectedIds : [])
        .map((x) => Number(x))
        .filter((x) => Number.isFinite(x) && x > 0)))
    );
    const mapSeed = {};
    (Array.isArray(initialSelectedRows) ? initialSelectedRows : []).forEach((row) => {
      const id = Number(row?.id_ban || 0);
      if (!id) return;
      mapSeed[id] = row;
    });
    setSelectedRowMap(mapSeed);
    setStatusOptions([]);
    setLocationOptions([]);
    setActiveStatusFilters([]);
    setActiveLocationFilters(forcedLocationsForInit.length ? forcedLocationsForInit : defaultLocationsForInit);
    initializingFiltersRef.current = true;
  }, [open, initialSelectedIds, initialSelectedRows, forcedLocationsDep, defaultLocationsDep]);

  useEffect(() => {
    if (!open) return;
    const forcedLocationsForEffect = forcedLocationsDep ? forcedLocationsDep.split("|").filter(Boolean) : [];
    const defaultLocationsForEffect = defaultLocationsDep ? defaultLocationsDep.split("|").filter(Boolean) : [];
    const locations = forcedLocationsForEffect.length
      ? forcedLocationsForEffect
      : (effectiveLocationFiltersDep ? effectiveLocationFiltersDep.split("|").filter(Boolean) : []);
    const locationSet = new Set((Array.isArray(locations) ? locations : []).map((x) => toLowerTrim(x)).filter(Boolean));
    const q = String(keyword || "").trim().toLowerCase();
    const baseRows = allLocalRows.filter((row) => {
      const place = String(row?.place_now || "").trim().toLowerCase();
      const locKey = place.startsWith("gudang#") ? "gudang" : (place.startsWith("pabrik#") ? "pabrik" : (/^k-/i.test(place) ? "otf" : ""));
      if (locationSet.size > 0 && !locationSet.has(locKey)) return false;
      if (!q) return true;
      return String(row?.id_ban || "").includes(q) || String(row?.seri_ban || "").toLowerCase().includes(q);
    });
    const statusesRaw = normalizeStatusList(baseRows.map((row) => row?.status_now));
    const locationsRaw = Array.from(new Set(baseRows.map((row) => {
      const place = String(row?.place_now || "").trim().toLowerCase();
      if (place.startsWith("gudang#")) return "gudang";
      if (place.startsWith("pabrik#")) return "pabrik";
      if (/^k-/i.test(place)) return "otf";
      return "";
    }).filter(Boolean)));
    setStatusOptions(statusesRaw);
    setLocationOptions(locationsRaw);
    setActiveStatusFilters((prev) => {
      if (initializingFiltersRef.current) return statusesRaw;
      const allowed = new Set(statusesRaw.map((status) => toUpperTrim(status)));
      return (Array.isArray(prev) ? prev : []).filter((status) => allowed.has(toUpperTrim(status)));
    });
    setActiveLocationFilters((prev) => {
      if (forcedLocationsForEffect.length) return forcedLocationsForEffect;
      if (initializingFiltersRef.current && defaultLocationsForEffect.length) {
        const allowed = new Set(locationsRaw.map((loc) => toLowerTrim(loc)));
        return defaultLocationsForEffect.filter((loc) => allowed.has(toLowerTrim(loc)));
      }
      if (initializingFiltersRef.current) return locationsRaw;
      const allowed = new Set(locationsRaw.map((loc) => toLowerTrim(loc)));
      return (Array.isArray(prev) ? prev : []).filter((loc) => allowed.has(toLowerTrim(loc)));
    });
    initializingFiltersRef.current = false;
    setLoading(false);
  }, [open, allLocalRows, forcedLocationsDep, defaultLocationsDep, effectiveLocationFiltersDep, keyword]);

  useEffect(() => {
    setTotal(localFilteredRows.length);
    const maxPage = Math.max(1, Math.ceil(localFilteredRows.length / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [localFilteredRows, page, pageSize]);

  const toggleStatus = (status) => {
    const key = toUpperTrim(status);
    setPage(1);
    setActiveStatusFilters((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const has = list.some((x) => toUpperTrim(x) === key);
      if (has) return list.filter((x) => toUpperTrim(x) !== key);
      return [...list, status];
    });
  };

  const toggleLocation = (loc) => {
    if (disableLocationFilterControls || forcedLocationsNormalized.length) return;
    const key = toLowerTrim(loc);
    if (blockedLocationsNormalized.includes(key)) return;
    setPage(1);
    setActiveLocationFilters((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const has = list.some((x) => toLowerTrim(x) === key);
      if (has) return list.filter((x) => toLowerTrim(x) !== key);
      return [...list, loc];
    });
  };

  const displayedRows = useMemo(() => {
    const start = (Math.max(1, page) - 1) * pageSize;
    return localFilteredRows.slice(start, start + pageSize);
  }, [localFilteredRows, page, pageSize]);

  if (!open) return null;

  return (
    <div className="page16-modal-backdrop" role="dialog" aria-modal="true">
      <div className="page16-modal-card" style={{ maxWidth: "min(1080px, 96vw)", width: "96vw" }}>
        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
          <div>
            <h5 className="mb-1">PILIH MULTI BAN</h5>
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Tutup
          </button>
        </div>

        <div className="row g-3">
          <div className="col-12">
            <label className="form-label">Filter</label>
            <input
              type="text"
              className="form-control"
              placeholder="Masukkan no. seri ban.."
              value={keyword}
              onChange={(e) => {
                setPage(1);
                setKeyword(e.target.value);
              }}
            />
          </div>

          <div className="col-12">
            <label className="form-label mb-1">Filter Status</label>
            <div className="d-flex flex-wrap gap-2">
              {statusOptions.map((status) => {
                const active = activeStatusFilters.some((x) => toUpperTrim(x) === toUpperTrim(status));
                return (
                  <button
                    key={`bulk-status-${status}`}
                    type="button"
                    className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => toggleStatus(status)}
                  >
                    {status}
                  </button>
                );
              })}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  setPage(1);
                  setActiveStatusFilters([]);
                }}
                disabled={activeStatusFilters.length === 0}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="col-12">
            <label className="form-label mb-1">Filter Lokasi</label>
            <div className="d-flex flex-wrap gap-2">
              {locationOptions.map((loc) => {
                const active = activeLocationFilters.some((x) => toLowerTrim(x) === toLowerTrim(loc));
                const label = loc === "otf" ? "OTF (K-*)" : (loc === "pabrik" ? "Pabrik" : "Gudang");
                const locKey = toLowerTrim(loc);
                const isForced = forcedLocationsNormalized.includes(locKey);
                const isBlocked = blockedLocationsNormalized.includes(locKey);
                const isDisabled = isBlocked || disableLocationFilterControls || forcedLocationsNormalized.length > 0;
                return (
                  <button
                    key={`bulk-location-${loc}`}
                    type="button"
                    className={`btn btn-sm ${(active || isForced) ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => toggleLocation(loc)}
                    disabled={isDisabled}
                  >
                    {label}
                  </button>
                );
              })}
              {!disableLocationFilterControls && forcedLocationsNormalized.length === 0 && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setPage(1);
                    setActiveLocationFilters([]);
                  }}
                  disabled={activeLocationFilters.length === 0}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="col-12">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                gap: "0.5rem",
                maxHeight: "52vh",
                overflowY: "auto",
              }}
            >
              {displayedRows.map((ban) => {
                const isChecked = selectedSet.has(Number(ban.id_ban));
                return (
                  <button
                    key={`pick-bulk-ban-${ban.id_ban}`}
                    type="button"
                    className="btn btn-sm text-start position-relative"
                    style={{
                      border: isChecked ? "1px solid #0f2d5c" : "1px solid #cbd5e1",
                      background: isChecked ? "#0f2d5c" : "#ffffff",
                      color: isChecked ? "#ffffff" : "#0f172a",
                      borderRadius: "10px",
                      padding: "0.35rem 0.65rem",
                      minHeight: "56px",
                    }}
                    onClick={() => {
                      const id = Number(ban.id_ban);
                      setSelectedRowMap((prev) => {
                        const next = { ...(prev || {}) };
                        if (next[id]) delete next[id];
                        else next[id] = ban;
                        return next;
                      });
                      setSelectedIds((prev) => {
                        const next = new Set((Array.isArray(prev) ? prev : []).map((x) => Number(x)).filter((x) => x > 0));
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                        return Array.from(next);
                      });
                    }}
                  >
                    <div className="pe-4">
                      <div style={{ fontWeight: 700, lineHeight: 1.2 }}>{ban.seri_ban || "-"}</div>
                      <small style={{ opacity: 0.95 }}>{toUpperTrim(ban.status_now || "-") || "-"}</small>
                    </div>
                  </button>
                );
              })}
            </div>
            {!loading && displayedRows.length === 0 && (
              <small className="text-muted d-block mt-2">Data ban tidak ditemukan.</small>
            )}
          </div>

          <div className="col-12 d-flex justify-content-between align-items-center border-top pt-3">
            <small className="text-muted">
              {`Terpilih: ${selectedIds.length} ban | Data: ${total} | Halaman ${page}/${totalPages}`}
            </small>
            <div className="d-flex align-items-center gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
                Prev
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>
                Next
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setSelectedIds([])} disabled={!selectedIds.length}>
                Clear
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => {
                  const idsOnPage = displayedRows
                    .map((row) => Number(row?.id_ban || 0))
                    .filter((x) => Number.isFinite(x) && x > 0);
                  if (!idsOnPage.length) return;
                  setSelectedRowMap((prev) => {
                    const next = { ...(prev || {}) };
                    displayedRows.forEach((row) => {
                      const id = Number(row?.id_ban || 0);
                      if (!id) return;
                      next[id] = row;
                    });
                    return next;
                  });
                  setSelectedIds((prev) => {
                    const next = new Set((Array.isArray(prev) ? prev : []).map((x) => Number(x)).filter((x) => x > 0));
                    idsOnPage.forEach((id) => next.add(id));
                    return Array.from(next);
                  });
                }}
                disabled={!displayedRows.length}
              >
                Pilih Semua
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  const ids = Array.from(
                    new Set(
                      (Array.isArray(selectedIds) ? selectedIds : [])
                        .map((x) => Number(x))
                        .filter((x) => Number.isFinite(x) && x > 0)
                    )
                  );
                  const hydratedRows = ids.map((id) => {
                    const rowFromMap = selectedRowMap[id];
                    const rowFromPage = (Array.isArray(displayedRows) ? displayedRows : []).find((x) => Number(x?.id_ban || 0) === id);
                    const row = rowFromMap || rowFromPage || { id_ban: id };
                    return {
                      id_ban: Number(row?.id_ban || 0),
                      seri_ban: String(row?.seri_ban || "").trim(),
                      kode_cabang: String(row?.kode_cabang || "").trim(),
                      status_now: String(row?.status_now || row?.status || "").trim(),
                      place_now: String(row?.place_now || row?.place || "").trim(),
                    };
                  });
                  const enrichedRows = await enrichSelectedRows({ ids, sourceRows: hydratedRows });
                  if (!enrichedRows) return;
                  const blockedRows = enrichedRows.filter((row) => isBanStatusBlockedByMt(row?.status_now));
                  if (blockedRows.length > 0) {
                    showNotif?.("error", "Sebagian ban berstatus out/nonaktif by MT dan tidak bisa ditransaksikan.");
                    return;
                  }
                  const filteredRows = enrichedRows.filter((row) => !isBanStatusBlockedByMt(row?.status_now));
                  const filteredIds = filteredRows
                    .map((row) => Number(row?.id_ban || 0))
                    .filter((id) => Number.isFinite(id) && id > 0);
                  onSubmit?.(filteredRows, filteredIds);
                  onClose?.();
                }}
                disabled={loading}
              >
                Selesai
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
